import { tok } from './risk'

/**
 * SCADA signal gauge — a semicircular "fill toward the limit" dial.
 *
 * The arc fills with the current value on a 0 → threshold×1.25 scale; a bold tick
 * marks the limit and a faint red band sits beyond it. The fill warms cool → amber
 * → red as the value nears and crosses the threshold, so **full + red = at/over the
 * limit** (never an empty gauge for danger, which the earlier headroom fill implied).
 * The centre shows the real value and its limit — not an abstract score.
 */
export function SignalGauge({
  value,
  threshold,
  unit,
  maxWidth = 172,
}: {
  value: number
  threshold: number
  unit: string
  maxWidth?: number
}) {
  const w = 220
  const h = 128
  const cx = w / 2
  const cy = 112
  const r = 82
  const scaleMax = threshold * 1.25 || 1
  const thrFrac = threshold / scaleMax // 0.8
  const valFrac = Math.max(0, Math.min(1, value / scaleMax))
  const over = value >= threshold

  const P = (f: number, rad = r): [number, number] => [
    cx - rad * Math.cos(Math.PI * f),
    cy - rad * Math.sin(Math.PI * f),
  ]
  const arc = (a: number, b: number) => {
    const [x1, y1] = P(a)
    const [x2, y2] = P(b)
    return `M${x1.toFixed(1)} ${y1.toFixed(1)} A${r} ${r} 0 0 1 ${x2.toFixed(1)} ${y2.toFixed(1)}`
  }
  const zone = value >= threshold ? '--ignition' : value >= 0.8 * threshold ? '--flare' : '--stable'
  const [tx0, ty0] = P(thrFrac, r - 9)
  const [tx1, ty1] = P(thrFrac, r + 9)

  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', maxWidth }}>
      <path d={arc(0, 1)} fill="none" stroke={tok('--surface-2')} strokeWidth={12} strokeLinecap="round" />
      <path d={arc(thrFrac, 1)} fill="none" stroke={tok('--ignition')} strokeOpacity={0.16} strokeWidth={12} />
      <path
        d={arc(0, Math.max(valFrac, 0.004))}
        fill="none"
        stroke={tok(zone)}
        strokeWidth={12}
        strokeLinecap="round"
      />
      <line x1={tx0} y1={ty0} x2={tx1} y2={ty1} stroke={tok('--ink')} strokeWidth={2} />

      {over && (
        <text
          x={cx}
          y={cy - 46}
          textAnchor="middle"
          fontSize={9}
          fontWeight={700}
          letterSpacing="0.08em"
          fontFamily="var(--font-display)"
          fill={tok('--ignition')}
        >
          OVER LIMIT
        </text>
      )}
      <text
        x={cx}
        y={cy - 24}
        textAnchor="middle"
        fontSize={24}
        fontWeight={600}
        fontFamily="var(--font-display)"
        fill={tok(over ? '--ignition' : '--ink')}
        className="tabular"
      >
        {value}
        <tspan fontSize={12} fill={tok('--faint')} dx={2}>
          {unit}
        </tspan>
      </text>
      <text
        x={cx}
        y={cy - 9}
        textAnchor="middle"
        fontSize={10}
        fontFamily="var(--font-display)"
        fill={tok('--faint')}
      >
        limit {threshold}
        {unit}
      </text>
    </svg>
  )
}
